import axios from 'axios';
import { toast } from 'react-toastify';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { mockEnabled, isDemoMode } from '../mockData/mockConfig';
import { mockHandlers } from '../mockData/mockHandlers';
import { triggerOfflineFromInterceptor } from 'context/NetworkProvider';

// NProgress config — thin top bar, no spinner
NProgress.configure({ showSpinner: false, speed: 400, minimum: 0.1 });

// ─────────────────────────────────────────────────────────────────────────────
// Mock resolver — endpoint ke liye mock response dhundta hai
// Agar endpoint ka mock nahi mila to warning deta hai aur generic response deta hai
// ─────────────────────────────────────────────────────────────────────────────
const resolveMock = (endpoint, data) => {
  const handler = mockHandlers[endpoint];
  if (!handler) {
    console.warn(`[MOCK] No mock defined for: ${endpoint}`);
    return Promise.resolve({ rescode: 0, message: `No mock data for ${endpoint}`, data: {} });
  }
  // Support function handlers for stateful mocks (e.g. settings update)
  if (typeof handler === 'function') return Promise.resolve(handler(data));
  return Promise.resolve(handler);
};

const portalAxios = axios.create({
  baseURL: process.env.REACT_APP_PUBLIC_API_URL,
});

portalAxios.interceptors.request.use((config) => {
  NProgress.start();
  const token = localStorage.getItem('token');
  const language = localStorage.getItem('language') || 'en';
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['Accept-Language'] = language;
  return config;
});

portalAxios.interceptors.response.use(
  (response) => { NProgress.done(); return response.data; },
  (error) => {
    NProgress.done();
    // Network error — no response from server
    if (!error.response) {
      // Only show offline popup if browser is actually offline
      if (!navigator.onLine) {
        triggerOfflineFromInterceptor();
      }
      return Promise.reject({
        success: false,
        type: "NETWORK_ERROR",
        message: navigator.onLine
          ? "Server unreachable. Please try again."
          : "You are offline. Please check your internet connection.",
      });
    }

    const status = error.response?.status;
    const data = error.response?.data;
    // A caller that shows its own message for a failure (config.skipErrorToast) gets no generic
    // toast on top of it. Signing out on 401 and the subscription notice on 402 still happen.
    const callerExplains = !!error.config?.skipErrorToast;
    if (status === 401 && !isDemoMode()) {
      // Demo preview (hero laptop / demo.html iframes) must never get bounced to
      // the marketing home page on an un-mocked 401 — it stays inside the portal.
      localStorage.removeItem('token');
      localStorage.removeItem('persist:root');
      window.location.href = '/';
    } else if (status === 402) {
      // Subscription expired/grace/locked — dispatch event so SubscriptionProvider can redirect
      window.dispatchEvent(new CustomEvent('subscription:expired', {
        detail: {
          no_subscription: data?.no_subscription || false,
          subscription_expired: data?.subscription_expired || false,
          trial_expired: data?.trial_expired || false,
          grace_period: data?.grace_period || false,
          grace_days_left: data?.grace_days_left || 0,
          account_locked: data?.account_locked || false,
          message: data?.message || 'Subscription expired',
        },
      }));
      // Show toast only once — multiple 402s fire on page load, avoid spamming
      if (!window._subscriptionToastShown) {
        window._subscriptionToastShown = true;
        if (data?.no_subscription) {
          toast.info('Choose a plan to activate your account.');
        } else if (data?.trial_expired) {
          toast.warning('Your free trial has ended. Please choose a plan to continue.');
        } else if (data?.grace_period) {
          toast.warning(`Subscription expired. ${data.grace_days_left || 0} days left to download your data.`);
        } else if (data?.account_locked) {
          toast.error('Your account is locked. Please renew your subscription.');
        } else {
          toast.warning('Your subscription has expired. Please renew your plan.');
        }
        // Reset after 10 seconds so toast can show again on next navigation
        setTimeout(() => { window._subscriptionToastShown = false; }, 10000);
      }
    } else if (status === 403) {
      if (data?.is_demo) {
        // Demo mode — show popup instead of toast
        window.dispatchEvent(new CustomEvent('demo:blocked'));
      } else if (isWriteRequest(error.config) && !callerExplains) {
        // A real user-initiated write (save/delete/assign/etc.) was blocked —
        // that's worth surfacing. Background reads (list/dropdown/widget data
        // fetched automatically on page load) fail silently instead: a role
        // missing a permission for one of several auto-fetched calls on a
        // page shouldn't pop up "access denied" toasts the user never
        // triggered — the page's own empty-state UI covers the missing data.
        // Deduped so several near-simultaneous blocked writes don't stack.
        if (!window._permissionToastShown) {
          window._permissionToastShown = true;
          toast.error(data?.message || "You don't have permission for this action.");
          setTimeout(() => { window._permissionToastShown = false; }, 4000);
        }
      }
    } else if (status >= 500 && !callerExplains) {
      toast.error("Server error. Please try again later.");
    }
    return Promise.reject(error);
  }
);

// Endpoint fragments that indicate a state-changing (write) action, as opposed
// to this app's convention of fetching lists/dropdowns/widget data via POST.
const WRITE_ENDPOINT_PATTERNS = ['/add', '/create', '/update', '/edit', '/delete', '/remove',
  '/save', '/mark-status', '/cancel', '/assign', '/upload', '/reset-db', '/resetPermissions',
  '/send-reminders', '/send-email', '/transition', '/bulk-upload',
  '/impersonate', '/branding/update'];

// Is this request a real write, or just this app's POST-based read convention?
// PUT/DELETE are always writes; POST is only a write if the endpoint matches
// one of the known mutating patterns above.
function isWriteRequest(config) {
  const method = (config?.method || '').toLowerCase();
  if (method === 'put' || method === 'delete') return true;
  return WRITE_ENDPOINT_PATTERNS.some(p => (config?.url || '').includes(p));
}

// Check if current user is demo and trying a save/write action
function isDemoWrite(endpoint) {
  try {
    const persisted = JSON.parse(localStorage.getItem('persist:root') || '{}');
    const auth = JSON.parse(persisted.auth || '{}');
    if (!auth.user?.is_demo) return false;
    return WRITE_ENDPOINT_PATTERNS.some(p => endpoint.includes(p));
  } catch (e) { return false; }
}

// `config` (optional) is passed on to axios — e.g. { withCredentials: true } for a request whose
// response sets a cookie, or { skipErrorToast: true } (see the response interceptor above).
class ApiService {
  post(endpoint, data = {}, config) {
    if (mockEnabled() && isDemoWrite(endpoint)) {
      window.dispatchEvent(new CustomEvent('demo:blocked'));
      return Promise.resolve({ rescode: 0, type: 'demo', message: 'Demo mode: this action is disabled.', is_demo: true, data: {} });
    }
    if (mockEnabled()) return resolveMock(endpoint, data);
    return config ? portalAxios.post(endpoint, data, config) : portalAxios.post(endpoint, data);
  }

  get(endpoint, params = {}, config = {}) {
    if (mockEnabled()) return resolveMock(endpoint, params);
    return portalAxios.get(endpoint, { ...config, params });
  }

  put(endpoint, data = {}) {
    if (mockEnabled()) return resolveMock(endpoint, data);
    return portalAxios.put(endpoint, data);
  }

  delete(endpoint) {
    if (mockEnabled()) return resolveMock(endpoint);
    return portalAxios.delete(endpoint);
  }

  upload(endpoint, formData) {
    if (mockEnabled()) return resolveMock(endpoint, formData);
    return portalAxios.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * Download a CSV file from backend export endpoint.
   * Uses responseType: 'blob' to bypass JSON interceptor and trigger browser download.
   */
  downloadCsv(endpoint, filename) {
    return portalAxios.post(endpoint, {}, { responseType: 'blob' })
      .then((data) => {
        const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      });
  }

  /**
   * Super Admin only: download a full mysqldump of this server's database.
   * Used to pull a backup from the live site down to a developer's machine
   * (then imported locally via the "Import DB from File" tool on the login page).
   */
  downloadDbBackup(filename) {
    return portalAxios.post('admin/auth/export-db', {}, { responseType: 'blob' })
      .then((data) => {
        const blob = new Blob([data], { type: 'application/sql' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      });
  }
}

export default new ApiService();
